/**
 * @fileoverview ResourceCard — vertical interactive card in the Control
 * Panel home's Resources row. Top: colored icon-background tile;
 * middle: h6 title + muted description; bottom: "Action →" or
 * "Action ↗" link (uses arrow-right for in-app, arrow-top-right for
 * external).
 *
 * Foundry mapping: `control-panel-app__card__1o33bj1 bp6-card
 * bp6-elevation-0 bp6-interactive` + `__icon-background` (with a tone
 * modifier: __violet / __turquoise / __gray) + `__text-section` +
 * `__title` + `__action`.
 */

import { H6, Icon } from "@blueprintjs/core";
import type { IconName } from "@blueprintjs/icons";
import type { ReactElement, ReactNode } from "react";

export type ResourceCardTone = "violet" | "turquoise" | "gray";

interface ResourceCardProps {
  icon: IconName;
  tone?: ResourceCardTone;
  title: string;
  description: ReactNode;
  actionLabel: string;
  /** When true, the action arrow is arrow-top-right (external). */
  external?: boolean;
  href?: string;
  onClick?: () => void;
  className?: string;
}

export function ResourceCard({
  icon,
  tone = "gray",
  title,
  description,
  actionLabel,
  external = false,
  href,
  onClick,
  className,
}: ResourceCardProps): ReactElement {
  const classes = [
    "bp6-card bp6-elevation-0 bp6-interactive",
    "bp-resource-card",
    `is-tone-${tone}`,
    className,
  ].filter(Boolean).join(" ");

  const body = (
    <>
      <div className={`is-tone-${tone}`} aria-hidden="true">
        <Icon icon={icon} size={20} />
      </div>
      <div>
        <H6>{title}</H6>
        <div className="bp6-text-muted">{description}</div>
        <span>
          {actionLabel}
          <Icon
            icon={external ? "arrow-top-right" : "arrow-right"}
            aria-label={external ? "Open in a new tab" : undefined}
          />
        </span>
      </div>
    </>
  );

  if (href !== undefined) {
    return (
      <a data-testid="resource-card"
        href={href}
        className={classes}
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      >
        {body}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={classes}>
      {body}
    </button>
  );
}
